import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { DashboardApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { notifyError, notifySuccess } from '../utils/statusShortcuts';
import './BookingPage.css';

const DEFAULT_TIME_ZONE = 'Europe/Oslo';
const DEFAULT_MEETING_DURATION = 20;
const DEFAULT_MIN_NOTICE = 120;
const DEFAULT_MAX_ADVANCE = 7 * 24 * 60;

const WEEKDAY_OPTIONS = [
  { value: 1, label: 'Mandag' },
  { value: 2, label: 'Tirsdag' },
  { value: 3, label: 'Onsdag' },
  { value: 4, label: 'Torsdag' },
  { value: 5, label: 'Fredag' },
  { value: 6, label: 'Lørdag' },
  { value: 0, label: 'Søndag' },
];

const formatTime = (value) => {
  if (!value) return '';
  if (typeof value === 'string' && /^\d{2}:\d{2}$/.test(value)) {
    return value;
  }
  if (typeof value === 'string' && /^\d{2}:\d{2}:\d{2}$/.test(value)) {
    return value.slice(0, 5);
  }
  return value;
};

const normalizeSchedule = (item) => {
  if (!item) return null;
  const windows = Array.isArray(item.windows)
    ? item.windows.map((window) => ({
        id: `w-${item.id}-${window.id ?? Math.random().toString(36).slice(2)}`,
        weekday: window.weekday,
        start_time: formatTime(window.start_time ?? window.start),
        end_time: formatTime(window.end_time ?? window.end),
      }))
    : [];

  const exceptions = Array.isArray(item.exceptions)
    ? item.exceptions.map((exception) => ({
        id: `x-${item.id}-${exception.id ?? Math.random().toString(36).slice(2)}`,
        date: exception.date ?? exception.exception_date ?? '',
        start_time: formatTime(exception.start_time ?? exception.start),
        end_time: formatTime(exception.end_time ?? exception.end),
        is_available: Boolean(exception.is_available),
        note: exception.note ?? '',
      }))
    : [];

  const attendeesRaw = item.default_attendees ?? item.defaultAttendees;
  const attendees = Array.isArray(attendeesRaw)
    ? attendeesRaw
    : typeof attendeesRaw === 'string' && attendeesRaw.trim()
      ? attendeesRaw.split(',').map((entry) => entry.trim()).filter(Boolean)
      : [];

  return {
    id: item.id,
    userId: item.user_id ?? item.userId,
    name: item.name,
    timeZone: item.time_zone ?? item.timeZone ?? DEFAULT_TIME_ZONE,
    meetingDurationMinutes: item.meeting_duration_minutes ?? item.meetingDurationMinutes ?? DEFAULT_MEETING_DURATION,
    minimumNoticeMinutes: item.minimum_notice_minutes ?? item.minimumNoticeMinutes ?? DEFAULT_MIN_NOTICE,
    maxAdvanceMinutes: item.max_advance_minutes ?? item.maxAdvanceMinutes ?? DEFAULT_MAX_ADVANCE,
    defaultAttendees: attendees,
    windows,
    exceptions,
    updatedAt: item.updated_at ?? item.updatedAt ?? null,
  };
};

const createDefaultWindows = () => ([
  { id: `w-${Math.random().toString(36).slice(2)}`, weekday: 1, start_time: '09:00', end_time: '15:00' },
  { id: `w-${Math.random().toString(36).slice(2)}`, weekday: 2, start_time: '09:00', end_time: '15:00' },
  { id: `w-${Math.random().toString(36).slice(2)}`, weekday: 3, start_time: '09:00', end_time: '15:00' },
  { id: `w-${Math.random().toString(36).slice(2)}`, weekday: 4, start_time: '09:00', end_time: '15:00' },
  { id: `w-${Math.random().toString(36).slice(2)}`, weekday: 5, start_time: '09:00', end_time: '14:00' },
]);

const createEmptyForm = () => ({
  id: null,
  name: '',
  timeZone: DEFAULT_TIME_ZONE,
  meetingDurationMinutes: DEFAULT_MEETING_DURATION,
  minimumNoticeMinutes: DEFAULT_MIN_NOTICE,
  maxAdvanceMinutes: DEFAULT_MAX_ADVANCE,
  defaultAttendeesText: '',
  windows: createDefaultWindows(),
  exceptions: [],
});

const BookingPage = () => {
  const { selectedCompanyId } = useAuth();
  const companyId = selectedCompanyId;
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [formMode, setFormMode] = useState(null);
  const [formState, setFormState] = useState(createEmptyForm);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    if (!companyId) return undefined;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const response = await DashboardApi.getBookingSchedules(companyId);
        if (cancelled) return;
        const rawList = Array.isArray(response?.schedules) ? response.schedules : [];
        const normalized = rawList.map(normalizeSchedule).filter(Boolean);
        setSchedules(normalized);
        setError(null);
      } catch (err) {
        if (cancelled) return;
        setSchedules([]);
        const message = err.message || 'Kunne ikke hente bookingplaner.';
        setError(message);
        notifyError(message, { title: 'Kunne ikke hente bookingplaner', source: 'Booking' });
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [companyId]);

  useEffect(() => {
    if (!success) return undefined;
    notifySuccess(success, { source: 'Booking' });
    const timer = setTimeout(() => setSuccess(null), 4000);
    return () => clearTimeout(timer);
  }, [success]);

  const handleResetForm = useCallback(() => {
    setFormState(createEmptyForm());
    setFormMode(null);
  }, []);

  const handleSelectForEdit = useCallback((schedule) => {
    if (!schedule) return;
    setFormState({
      id: schedule.id,
      name: schedule.name || '',
      timeZone: schedule.timeZone || DEFAULT_TIME_ZONE,
      meetingDurationMinutes: schedule.meetingDurationMinutes || DEFAULT_MEETING_DURATION,
      minimumNoticeMinutes: schedule.minimumNoticeMinutes ?? DEFAULT_MIN_NOTICE,
      maxAdvanceMinutes: schedule.maxAdvanceMinutes ?? DEFAULT_MAX_ADVANCE,
      defaultAttendeesText: schedule.defaultAttendees.join(', '),
      windows: schedule.windows.length > 0
        ? schedule.windows.map((window) => ({ ...window, id: window.id || `w-${Math.random().toString(36).slice(2)}` }))
        : createDefaultWindows(),
      exceptions: schedule.exceptions.map((exception) => ({
        ...exception,
        id: exception.id || `x-${Math.random().toString(36).slice(2)}`,
      })),
    });
    setFormMode('edit');
  }, []);

  const handleCreateNew = useCallback(() => {
    setFormState(createEmptyForm());
    setFormMode('create');
  }, []);

  const updateWindow = (id, field, value) => {
    setFormState((prev) => ({
      ...prev,
      windows: prev.windows.map((window) => (
        window.id === id
          ? { ...window, [field]: field === 'weekday' ? Number(value) : value }
          : window
      )),
    }));
  };

  const addWindow = () => {
    setFormState((prev) => ({
      ...prev,
      windows: [
        ...prev.windows,
        {
          id: `w-${Math.random().toString(36).slice(2)}`,
          weekday: 1,
          start_time: '09:00',
          end_time: '15:00',
        },
      ],
    }));
  };

  const removeWindow = (id) => {
    setFormState((prev) => ({
      ...prev,
      windows: prev.windows.filter((window) => window.id !== id),
    }));
  };

  const updateException = (id, field, value) => {
    setFormState((prev) => ({
      ...prev,
      exceptions: prev.exceptions.map((exception) => (
        exception.id === id
          ? { ...exception, [field]: field === 'is_available' ? value : value }
          : exception
      )),
    }));
  };

  const addException = () => {
    const today = new Date().toISOString().slice(0, 10);
    setFormState((prev) => ({
      ...prev,
      exceptions: [
        ...prev.exceptions,
        {
          id: `x-${Math.random().toString(36).slice(2)}`,
          date: today,
          start_time: '00:00',
          end_time: '23:59',
          is_available: false,
          note: '',
        },
      ],
    }));
  };

  const removeException = (id) => {
    setFormState((prev) => ({
      ...prev,
      exceptions: prev.exceptions.filter((exception) => exception.id !== id),
    }));
  };

  const refreshSchedules = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const response = await DashboardApi.getBookingSchedules(companyId);
      const rawList = Array.isArray(response?.schedules) ? response.schedules : [];
      const normalized = rawList.map(normalizeSchedule).filter(Boolean);
      setSchedules(normalized);
      setError(null);
    } catch (err) {
      setSchedules([]);
      const message = err.message || 'Kunne ikke hente bookingplaner.';
      setError(message);
      notifyError(message, { title: 'Kunne ikke hente bookingplaner', source: 'Booking' });
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!companyId) return;

    const attendees = formState.defaultAttendeesText
      .split(',')
      .map((entry) => entry.trim())
      .filter((entry) => entry.length > 0);

    const windowsPayload = formState.windows
      .filter((window) => window.start_time && window.end_time)
      .map((window) => ({
        weekday: Number(window.weekday),
        start_time: window.start_time,
        end_time: window.end_time,
      }));

    const exceptionsPayload = formState.exceptions
      .filter((exception) => exception.date && exception.start_time && exception.end_time)
      .map((exception) => ({
        date: exception.date,
        start_time: exception.start_time,
        end_time: exception.end_time,
        is_available: Boolean(exception.is_available),
        note: exception.note || null,
      }));

    const payload = {
      name: formState.name,
      time_zone: formState.timeZone,
      meeting_duration_minutes: Number(formState.meetingDurationMinutes) || DEFAULT_MEETING_DURATION,
      minimum_notice_minutes: Number(formState.minimumNoticeMinutes) || DEFAULT_MIN_NOTICE,
      max_advance_minutes: Number(formState.maxAdvanceMinutes) || DEFAULT_MAX_ADVANCE,
      default_attendees: attendees,
      availability_windows: windowsPayload,
      exceptions: exceptionsPayload,
    };

    try {
      setSaving(true);
      if (formMode === 'edit' && formState.id) {
        await DashboardApi.updateBookingSchedule(formState.id, payload);
        setSuccess('Bookingplan oppdatert.');
      } else {
        await DashboardApi.createBookingSchedule(companyId, payload);
        setSuccess('Bookingplan opprettet.');
      }
      await refreshSchedules();
      handleResetForm();
    } catch (err) {
      const message = err.message || 'Kunne ikke lagre bookingplan.';
      setError(message);
      notifyError(message, { title: 'Kunne ikke lagre bookingplan', source: 'Booking' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (schedule) => {
    if (!schedule) return;
    if (!window.confirm(`Slett bookingplan "${schedule.name}"?`)) {
      return;
    }
    try {
      setDeletingId(schedule.id);
      await DashboardApi.deleteBookingSchedule(schedule.id);
      setSuccess('Bookingplan slettet.');
      if (formState.id === schedule.id) {
        handleResetForm();
      }
      await refreshSchedules();
    } catch (err) {
      const message = err.message || 'Kunne ikke slette bookingplan.';
      setError(message);
      notifyError(message, { title: 'Kunne ikke slette bookingplan', source: 'Booking' });
    } finally {
      setDeletingId(null);
    }
  };

  const allowSave = useMemo(() => {
    if (!formMode) return false;
    if (!formState.name.trim()) return false;
    if (!formState.timeZone.trim()) return false;
    if (!formState.windows.length) return false;
    return true;
  }, [formMode, formState.name, formState.timeZone, formState.windows.length]);

  return (
    <div className="booking-page">
      <header className="booking-header">
        <div>
          <h1>Bookingplaner</h1>
          <p>Administrer tilgjengelighet, varighet og standardinvitasjoner.</p>
        </div>
        <button type="button" className="btn primary" onClick={handleCreateNew}>
          + Ny bookingplan
        </button>
      </header>

      <div className="booking-layout">
        <section className="booking-list" aria-label="Eksisterende bookingplaner">
          {loading ? (
            <p>Laster bookingplaner…</p>
          ) : schedules.length === 0 ? (
            <p>Ingen bookingplaner er opprettet ennå. Klikk på «Ny bookingplan» for å komme i gang.</p>
          ) : (
            <ul>
              {schedules.map((schedule) => (
                <li key={schedule.id} className={formState.id === schedule.id ? 'active' : ''}>
                  <button type="button" onClick={() => handleSelectForEdit(schedule)}>
                    <strong>{schedule.name}</strong>
                    <span>{schedule.timeZone}</span>
                    <span>{schedule.defaultAttendees.length ? `${schedule.defaultAttendees.length} faste inviterte` : 'Ingen faste inviterte'}</span>
                  </button>
                  <div className="booking-list-actions">
                    <button type="button" className="btn ghost" onClick={() => handleSelectForEdit(schedule)}>
                      Rediger
                    </button>
                    <button
                      type="button"
                      className="btn danger"
                      onClick={() => handleDelete(schedule)}
                      disabled={deletingId === schedule.id}
                    >
                      {deletingId === schedule.id ? 'Sletter…' : 'Slett'}
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="booking-form" aria-label="Rediger bookingplan">
          {formMode ? (
            <form onSubmit={handleSubmit}>
              <div className="form-grid">
                <label htmlFor="booking-name">
                  Navn
                  <input
                    id="booking-name"
                    type="text"
                    value={formState.name}
                    onChange={(event) => setFormState((prev) => ({ ...prev, name: event.target.value }))}
                    required
                  />
                </label>
                <label htmlFor="booking-timezone">
                  Tidsone
                  <input
                    id="booking-timezone"
                    type="text"
                    value={formState.timeZone}
                    onChange={(event) => setFormState((prev) => ({ ...prev, timeZone: event.target.value }))}
                    required
                  />
                </label>
            <label htmlFor="booking-duration">
              Møtelengde (minutter)
              <input
                id="booking-duration"
                type="number"
                    min="5"
                    value={formState.meetingDurationMinutes}
                    onChange={(event) => setFormState((prev) => ({ ...prev, meetingDurationMinutes: event.target.value }))}
                  />
                </label>
                <label htmlFor="booking-min-notice">
                  Minstetid før møte (minutter)
                  <input
                    id="booking-min-notice"
                    type="number"
                    min="0"
                    value={formState.minimumNoticeMinutes}
                    onChange={(event) => setFormState((prev) => ({ ...prev, minimumNoticeMinutes: event.target.value }))}
                  />
                </label>
                <label htmlFor="booking-max-advance">
                  Maks frem i tid (minutter)
                  <input
                    id="booking-max-advance"
                    type="number"
                    min="60"
                    value={formState.maxAdvanceMinutes}
                    onChange={(event) => setFormState((prev) => ({ ...prev, maxAdvanceMinutes: event.target.value }))}
                  />
                </label>
                <label htmlFor="booking-attendees">
                  Standardinviterte (kommaseparert)
                  <input
                    id="booking-attendees"
                    type="text"
                    value={formState.defaultAttendeesText}
                    onChange={(event) => setFormState((prev) => ({ ...prev, defaultAttendeesText: event.target.value }))}
                    placeholder="fornavn@bedrift.no, support@bedrift.no"
                  />
                </label>
              </div>

              <fieldset className="booking-subsection">
                <legend>Tidspunkt per ukedag</legend>
                <p className="help-text">Legg til hvilke intervaller som kan bookes for hver ukedag.</p>
                <div className="booking-table">
                  <div className="booking-table-header">
                    <span>Ukedag</span>
                    <span>Fra</span>
                    <span>Til</span>
                    <span aria-hidden="true" />
                  </div>
                  {formState.windows.map((window) => (
                    <div className="booking-table-row" key={window.id}>
                      <select
                        value={window.weekday}
                        onChange={(event) => updateWindow(window.id, 'weekday', Number(event.target.value))}
                      >
                        {WEEKDAY_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                      <input
                        type="time"
                        value={window.start_time}
                        onChange={(event) => updateWindow(window.id, 'start_time', event.target.value)}
                        required
                      />
                      <input
                        type="time"
                        value={window.end_time}
                        onChange={(event) => updateWindow(window.id, 'end_time', event.target.value)}
                        required
                      />
                      <button type="button" className="btn ghost" onClick={() => removeWindow(window.id)}>
                        Fjern
                      </button>
                    </div>
                  ))}
                </div>
                <button type="button" className="btn secondary" onClick={addWindow}>
                  + Legg til tid
                </button>
              </fieldset>

              <fieldset className="booking-subsection">
                <legend>Unntak (datoer med annen tilgjengelighet)</legend>
                <div className="booking-table">
                  <div className="booking-table-header">
                    <span>Dato</span>
                    <span>Fra</span>
                    <span>Til</span>
                    <span>Status</span>
                    <span aria-hidden="true" />
                  </div>
                  {formState.exceptions.map((exception) => (
                    <div className="booking-table-row" key={exception.id}>
                      <input
                        type="date"
                        value={exception.date}
                        onChange={(event) => updateException(exception.id, 'date', event.target.value)}
                        required
                      />
                      <input
                        type="time"
                        value={exception.start_time}
                        onChange={(event) => updateException(exception.id, 'start_time', event.target.value)}
                        required
                      />
                      <input
                        type="time"
                        value={exception.end_time}
                        onChange={(event) => updateException(exception.id, 'end_time', event.target.value)}
                        required
                      />
                      <label className="toggle">
                        <input
                          type="checkbox"
                          checked={exception.is_available}
                          onChange={(event) => updateException(exception.id, 'is_available', event.target.checked)}
                        />
                        <span>{exception.is_available ? 'Tilgjengelig' : 'Opptatt'}</span>
                      </label>
                      <button type="button" className="btn ghost" onClick={() => removeException(exception.id)}>
                        Fjern
                      </button>
                    </div>
                  ))}
                </div>
                <button type="button" className="btn secondary" onClick={addException}>
                  + Legg til unntak
                </button>
              </fieldset>

              <div className="booking-actions">
                <button type="submit" className="btn primary" disabled={!allowSave || saving}>
                  {saving ? 'Lagrer…' : 'Lagre plan'}
                </button>
                <button type="button" className="btn ghost" onClick={handleResetForm}>
                  Avbryt
                </button>
              </div>
            </form>
          ) : (
            <div className="booking-placeholder">
              <p>Velg en bookingplan fra listen eller opprett en ny for å konfigurere tilgjengelighet.</p>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default BookingPage;
